import { useRecordContext, ArrayField, SingleFieldList } from 'react-admin';
import { Box, Typography, Grid, IconButton, Divider, Paper } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { AttributeItem } from './AttributeItem';
import JSZip from "jszip";
import { saveAs } from "file-saver";

const ImageZipDownload = () => {
  const record = useRecordContext();
  if (!record?.images || !Array.isArray(record.images)) return null;

  const handleDownload = async () => {
    const zip = new JSZip();

    for (const [index, img] of record.images.entries()) {
      // 處理 imageUrl
      if (img.imageUrl) {
        try {
          const url = img.imageUrl;
          const response = await fetch(url);
          const blob = await response.blob();

          // 從 URL 中提取檔案名稱
          const urlObj = new URL(url);
          const filenameWithTimestamp = decodeURIComponent(urlObj.pathname.split('/').pop() || '');
          
          // 移除檔名中的 -timestamp（例如：行銷圖-1722851071234.png -> 行銷圖.png）
          const filenameWithoutTimestamp = filenameWithTimestamp.replace(/-\d{10,}(?=\.\w+$)/, '');

          let extension = "jpg";
          const contentType = response.headers.get("content-type");

          if (contentType) {
            if (contentType.includes("png")) extension = "png";
            else if (contentType.includes("jpeg")) extension = "jpg";
            else if (contentType.includes("gif")) extension = "gif";
            else if (contentType.includes("webp")) extension = "webp";
          } else {
            // 若無 content-type，從 URL 解析副檔名
            const urlExt = filenameWithoutTimestamp.split('.').pop().split(/\#|\?/)[0];
            extension = urlExt || 'jpg';
          }

          // 確保檔案副檔名正確
          const finalFileName = filenameWithoutTimestamp.replace(/\.[^/.]+$/, `.${extension}`);

          // 將檔案加入 ZIP
          zip.file(finalFileName, blob);
        } catch (err) {
          console.warn(`下載 imageUrl 失敗: ${img.imageUrl}`, err);
        }
      }

      // 處理 logomageUrl
      for (const [logoIndex, logoImg] of img.logoImages.entries()) {
        if (logoImg.imageUrl) {
          try {
            const url = logoImg.imageUrl;
            const response = await fetch(url);
            const blob = await response.blob();

            // 從 URL 提取原始檔案名稱
            const urlObj = new URL(url);
            const filenameWithTimestamp = decodeURIComponent(urlObj.pathname.split('/').pop() || '');

            // 移除 -timestamp（例如：行銷圖-1722851071234.png → 行銷圖.png）
            const filenameWithoutTimestamp = filenameWithTimestamp.replace(/-\d{10,}(?=\.\w+$)/, '');

            let extension = "jpg";
            const contentType = response.headers.get("content-type");
            if (contentType) {
              if (contentType.includes("png")) extension = "png";
              else if (contentType.includes("jpeg")) extension = "jpg";
              else if (contentType.includes("gif")) extension = "gif";
              else if (contentType.includes("webp")) extension = "webp";
            } else {
              // 若無 content-type，從 URL 副檔名 fallback
              const urlExt = filenameWithoutTimestamp.split('.').pop().split(/\#|\?/)[0];
              extension = urlExt || 'jpg';
            }

            // 最終檔名，確保副檔名正確
            const finalFileName = filenameWithoutTimestamp.replace(/\.[^/.]+$/, `.${extension}`);

            // 加入 ZIP
            zip.file(finalFileName, blob);
          } catch (err) {
            console.warn(`下載 logoImageUrl 失敗: ${logoImg.imageUrl}`, err);
          }
        }
      }
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    saveAs(zipBlob, `${record.productName}.zip`);
  };

  return (
    <IconButton onClick={handleDownload} size="small">
      <DownloadIcon fontSize="small" />
    </IconButton>
  );
};

const SizeTable = ({ specificationOrderCount, cellWidth = 80, labelWidth = 120 }) => {
  const rows = Array.isArray(specificationOrderCount) ? specificationOrderCount : [];
  const cleaned = rows
    .map(({ specification, count }) => {
      const key = String(specification ?? "").toUpperCase().trim();
      const n = Number(count);
      return key ? { key, count: Number.isFinite(n) ? n : 0 } : null;
    })
    .filter(Boolean);

  if (cleaned.length === 0) return null;

  const defaultOrder = ["XS", "S", "M", "L", "XL", "2XL", "3XL"];
  const presentSet = new Set(cleaned.map(r => r.key));
  const extrasInInputOrder = cleaned
    .map(r => r.key)
    .filter(k => !defaultOrder.includes(k))
    .filter((k, i, arr) => arr.indexOf(k) === i);

  const columns = [
    ...defaultOrder.filter(k => presentSet.has(k)),
    ...extrasInInputOrder,
  ];

  const countMap = Object.fromEntries(cleaned.map(({ key, count }) => [key, count]));
  const tableWidth = columns.length * cellWidth; // 固定總寬

  return (
    <Box sx={{ overflowX: 'auto', mb: 1 }}>
      <Box sx={{ display: 'flex', width: labelWidth + tableWidth }}>
        {/* 左側固定寬度標題 */}
        <Box
          sx={{
            width: labelWidth,
            flex: '0 0 auto',
            display: 'flex',
            alignItems: 'center',
            pr: 1,
          }}
        >
          <Typography variant="subtitle1" color="text.secondary">
            規格
          </Typography>
        </Box>

        {/* 右側固定欄寬表格 */}
        <table
          style={{
            borderCollapse: 'collapse',
            tableLayout: 'fixed',     // ★ 固定欄寬關鍵
            width: tableWidth,        // ★ 設定總寬
          }}
        >
          <colgroup>
            {columns.map((_, i) => (
              <col key={i} style={{ width: cellWidth }} />  // ★ 每欄固定寬
            ))}
          </colgroup>
          <thead>
            <tr>
              {columns.map(size => (
                <th
                  key={size}
                  style={{
                    padding: '4px 8px',
                    borderBottom: '1px solid #ccc',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {size}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {columns.map(size => (
                <td
                  key={size}
                  style={{
                    textAlign: 'center',
                    padding: '4px 8px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {countMap[size] ?? 0}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </Box>
    </Box>
  );
};

const isClothesProduct = (record) =>
  Array.isArray(record?.specificationOrderCount);

// 單一訂購商品的卡片元件
const OrderItemCard = () => {
    const record = useRecordContext();
    if (!record) return null;

    const isClothes = isClothesProduct(record);

    const purchaseTypeMaps = {
      blank_sample: "空白樣品",
      sample: "客製打樣",
      bulk: "大宗採購"
    }

    return (
        <Box sx={{ py: 2, width: '100%' }}>
            <Grid container spacing={2} alignItems="center">
                {/* 下載圖示 */}
                <Grid item xs={1} sx={{ textAlign: 'center' }}>
                    <ImageZipDownload />
                </Grid>
                
                {/* 商品圖片 */}
                <Grid item xs={2}>
                    <Box
                        component="img"
                        src={record.images?.[0]?.imageUrl}
                        alt={record.images?.[0]?.fileName}
                        sx={{
                            width: '100%',
                            maxWidth: '100px',
                            height: 'auto',
                            objectFit: 'cover',
                            borderRadius: '8px',
                            border: '1px solid #eee'
                        }}
                    />
                </Grid>

                {/* 商品詳細資訊 */}
                <Grid item xs={9}>
                    <Typography variant="h6" gutterBottom>
                        {record.productName}
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={3} sx={{ borderBottom: "1px solid #e0e0e0" }}>
                            <AttributeItem label="顏色" value={record.color} />
                        </Grid>
                        <Grid item xs={12} md={3} sx={{ borderBottom: "1px solid #e0e0e0" }}>
                            <AttributeItem label="印製方式" value={record.printingMethod} />
                        </Grid>
                        <Grid item xs={12} md={3} sx={{ borderBottom: "1px solid #e0e0e0" }}>
                            <AttributeItem label="訂單種類" value={purchaseTypeMaps[record.purchaseType]} />
                        </Grid>
                        { record.purchaseType && 
                          <Grid item xs={12} md={3} sx={{ borderBottom: "1px solid #e0e0e0" }}>
                            <AttributeItem label="打樣時間" value={`${record.samplingTime}天`} />
                          </Grid> 
                        }
                        <Grid item xs={12} sx={{ borderBottom: "1px solid #e0e0e0" }}>
                            {isClothes ? (
                              <SizeTable specificationOrderCount={record.specificationOrderCount} />
                            ) : (
                              <AttributeItem label="規格" value={record.specification} />
                            )}
                        </Grid>
                        <Grid item xs={12} md={3} sx={{ borderBottom: "1px solid #e0e0e0" }}>
                            <AttributeItem label="訂購數量" value={record.count} />
                        </Grid>
                        <Grid item xs={12} md={3} sx={{ borderBottom: "1px solid #e0e0e0" }}>
                            <AttributeItem
                              label="商品單價"
                              value={
                                (record.purchaseType === 'sample'
                                  ? record.samplingFee ?? 0
                                  : record.unitPrice ?? 0
                                ).toLocaleString('zh-TW', {
                                  style: 'currency',
                                  currency: 'TWD',
                                  minimumFractionDigits: 0,
                                })
                              }
                            />
                        </Grid>
                        <Grid item xs={12} md={6} sx={{ borderBottom: "1px solid #e0e0e0", display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', gap: 3 }}>
                          <AttributeItem 
                            label="總計" 
                            value={
                              (record.purchaseType === 'bulk'
                                ? record.origineTotalPrice ?? 0
                                : record.totalPrice ?? 0
                              ).toLocaleString('zh-TW', {
                                style: 'currency',
                                currency: 'TWD',
                                minimumFractionDigits: 0,
                              })
                            }
                          />
                          {record.purchaseType === 'bulk' && (
                            <AttributeItem 
                              label="優惠價" 
                              value={
                                record.totalPrice.toLocaleString('zh-TW', {
                                  style: 'currency',
                                  currency: 'TWD',
                                  minimumFractionDigits: 0
                                })
                              } 
                              highlight
                            />
                          )}
                        </Grid>
                        
                        {/* <Grid item xs={12} md={3}>
                            <AttributeItem label="顏色" value={record.color} />
                            <AttributeItem label="訂單種類" value={purchaseTypeMaps[record.purchaseType]} />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <AttributeItem label="規格" value={record.specification} />
                            <AttributeItem label="訂購數量" value={record.count} />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <AttributeItem label="印製方式" value={record.printingMethod} />
                            <AttributeItem
                              label="商品單價"
                              value={
                                (record.purchaseType === 'sample'
                                  ? record.samplingFee ?? 0
                                  : record.unitPrice ?? 0
                                ).toLocaleString('zh-TW', {
                                  style: 'currency',
                                  currency: 'TWD',
                                  minimumFractionDigits: 0,
                                })
                              }
                            />
                        </Grid>
                        <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                          <AttributeItem 
                            label="總計" 
                            value={
                              (record.purchaseType === 'bulk'
                                ? record.origineTotalPrice ?? 0
                                : record.totalPrice ?? 0
                              ).toLocaleString('zh-TW', {
                                style: 'currency',
                                currency: 'TWD',
                                minimumFractionDigits: 0,
                              })
                            } 
                          />
                          {record.purchaseType === 'bulk' && (
                            <AttributeItem 
                              label="優惠價" 
                              value={
                                record.totalPrice.toLocaleString('zh-TW', {
                                  style: 'currency',
                                  currency: 'TWD',
                                  minimumFractionDigits: 0
                                })
                              } 
                            />
                          )}
                        </Grid> */}
                    </Grid>
                </Grid>
            </Grid>
        </Box>
    );
};

// 訂單金額總計的元件
const OrderSummary = () => {
    const record = useRecordContext();
    if (!record || !record.orderItems) return null;
    
    const subtotal = record.orderItems.reduce((total, item) => total + (item.totalPrice), 0);

    const shippingFee = record.deliveryFee || 0;
    const totalAmount = record.purchaseTotalPrice || 0;

    return (
        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <Box sx={{ minWidth: '250px' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1">商品金額</Typography>
                    <Typography variant="body1" fontWeight="bold">
                        {subtotal.toLocaleString('zh-TW', { style: 'currency', currency: 'TWD', minimumFractionDigits: 0 })}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1">運費</Typography>
                    <Typography variant="body1" fontWeight="bold">
                        {shippingFee.toLocaleString('zh-TW', { style: 'currency', currency: 'TWD', minimumFractionDigits: 0 })}
                    </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6">訂單金額</Typography>
                    <Typography variant="h6" fontWeight="bold" color="error.main">
                        {totalAmount.toLocaleString('zh-TW', { style: 'currency', currency: 'TWD', minimumFractionDigits: 0 })}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

export const OrderItemsList = () => {
    return (
        <Paper elevation={2} sx={{ p: 3, width: 1200, mt: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
                訂購商品
            </Typography>
            <ArrayField source="orderItems">
                <SingleFieldList linkType={false} component="div">
                    <OrderItemCard />
                </SingleFieldList>
            </ArrayField>
            <OrderSummary />
        </Paper>
    );
};